/**
 * Zones offered when the runtime cannot enumerate them. Deliberately short: it
 * is a floor for an old browser, not an attempt to restate the IANA database.
 */
const FALLBACK_TIME_ZONES = [
  'America/Anchorage',
  'America/Chicago',
  'America/Denver',
  'America/Edmonton',
  'America/Halifax',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Monterrey',
  'America/New_York',
  'America/Phoenix',
  'America/Regina',
  'America/St_Johns',
  'America/Tijuana',
  'America/Toronto',
  'America/Vancouver',
  'America/Winnipeg',
  'Pacific/Honolulu',
];

type IntlWithSupportedValues = {
  readonly supportedValuesOf?: (key: 'timeZone') => string[];
};

/**
 * The backend accepts canonical IANA region zones only, and rejects fixed
 * offsets, abbreviations and `Etc/*` because they are blind to daylight saving.
 * Filtering here keeps a value that would be refused out of the picker.
 */
const isRegionTimeZone = (timeZone: string): boolean =>
  timeZone.includes('/') && !timeZone.startsWith('Etc/');

/**
 * Region time zones a resource may be read in, with `alwaysInclude` folded in so
 * a zone already stored on a resource is selectable even when this runtime does
 * not list it.
 */
export const listRegionTimeZones = (
  alwaysInclude: ReadonlyArray<string | null | undefined> = [],
): string[] => {
  const { supportedValuesOf } = Intl as IntlWithSupportedValues;
  const supported =
    supportedValuesOf === undefined
      ? FALLBACK_TIME_ZONES
      : supportedValuesOf('timeZone');
  const zones = new Set(supported.filter((zone) => isRegionTimeZone(zone)));
  for (const zone of alwaysInclude) {
    if (typeof zone === 'string' && zone.length > 0) {
      zones.add(zone);
    }
  }
  return [...zones].sort((a, b) => a.localeCompare(b));
};
